import React, { useCallback } from 'react';
import proj4 from 'proj4';
import CustomAutocomplete from '../../CustomAutocomplete';
import apiConfig from '../../../config/api';
import Field from '../components/Field';
import StatusBadge from '../components/StatusBadge';
import VerifyTools from '../components/VerifyTools';
import {
  PROJECT_TYPES,
  PLAN_ROLE,
  PLAN_ROLE_OPTIONS,
  STATUS,
  field,
  isFilled,
  makeSiteElement,
  makeDocument,
  DOC_KINDS,
} from '../state/model';

// EPSG:2039 (Israeli ITM) — same definition used across the app (ChatAssistant,
// LocationMap). Needed to convert the autocomplete's WGS84 point to ITM before
// hitting /api/govmap-layers for the parcel (gush/helka).
proj4.defs('EPSG:2039', '+proj=tmerc +lat_0=31.73439361111111 +lon_0=35.20451694444445 +k=1.0000067 +x_0=219529.584 +y_0=626907.39 +ellps=GRS80 +towgs84=-48,55,52,0,0,0,0 +units=m +no_defs');
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');

export default function PropertyTab({ project, updateProject, onAutofill, autofilling, autofillError }) {
  const prop = project.property;
  const proj = project.project;

  const setField = useCallback((section, key, partial) => {
    updateProject((p) => {
      p[section][key] = { ...p[section][key], ...partial };
    }, `עודכן שדה: ${key}`);
  }, [updateProject]);

  const approveAllProperty = useCallback(() => {
    updateProject((p) => {
      for (const k of PROP_VERIFY_KEYS) {
        if (p.property[k]?.status === STATUS.AUTO) p.property[k] = { ...p.property[k], status: STATUS.APPROVED };
      }
    }, 'אושרו כל החילוצים — נתוני נכס');
  }, [updateProject]);

  const addDocument = useCallback(() => {
    updateProject((p) => { p.documents.push(makeDocument({ kind: 'אחר' })); }, 'נוסף מסמך לספרייה');
  }, [updateProject]);

  const updateDocument = useCallback((id, patch) => {
    updateProject((p) => {
      const d = p.documents.find((x) => x.id === id);
      if (d) Object.assign(d, patch);
    });
  }, [updateProject]);

  const removeDocument = useCallback((id) => {
    updateProject((p) => { p.documents = p.documents.filter((x) => x.id !== id); }, 'הוסר מסמך מהספרייה');
  }, [updateProject]);

  const setRole = useCallback((id, partial) => {
    updateProject((p) => {
      const item = p.planStack.find((x) => x.id === id);
      if (item) item.role = { ...item.role, ...partial };
    }, 'עודכן תפקיד תכנית');
  }, [updateProject]);

  // Address → ITM → parcel. Mirrors ChatAssistant.handleAddressSelected, but
  // writes resolved values into property fields tagged "חולץ אוטומטית".
  const handleAddressSelected = useCallback(async (placeResult) => {
    const loc = placeResult?.geometry?.location;
    if (!loc) return;
    const lat = typeof loc.lat === 'function' ? loc.lat() : (loc.lat ?? loc.latitude);
    const lng = typeof loc.lng === 'function' ? loc.lng() : (loc.lng ?? loc.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const text = placeResult.formatted_address || placeResult.name;
    try {
      const [x_itm, y_itm] = proj4('EPSG:4326', 'EPSG:2039', [Number(lng), Number(lat)]);
      const resp = await fetch(`${apiConfig.apiBaseUrl}/api/govmap-layers?x=${x_itm}&y=${y_itm}`);
      const data = await resp.json();
      const ownership = Array.isArray(data?.ownership) && data.ownership.length > 0 ? data.ownership[0] : null;
      const gush = ownership?.['גוש'] != null ? String(ownership['גוש']) : null;
      const helka = ownership?.['חלקה'] != null ? String(ownership['חלקה']) : null;
      updateProject((p) => {
        p.property.address = field(text, { status: STATUS.AUTO, source: 'Google Maps' });
        p.property.coords = { x_itm, y_itm };
        p.property.gmapsUrl = field(`https://www.google.com/maps?q=${lat},${lng}`, { status: STATUS.AUTO, source: 'Google Maps' });
        if (gush) p.property.gush = field(gush, { status: STATUS.AUTO, source: 'GovMap' });
        if (helka) p.property.helka = field(helka, { status: STATUS.AUTO, source: 'GovMap' });
        // Geometric parcel area from the cadastre polygon — primary source;
        // the tik-binyan extraction cross-checks it during autofill.
        const govmapViewerUrl = `https://www.govmap.gov.il/?c=${Math.round(x_itm)},${Math.round(y_itm)}&z=10`;
        if (Number.isFinite(data?.parcelAreaM2) && data.parcelAreaM2 > 0) {
          p.property.parcelArea = field(data.parcelAreaM2, {
            status: STATUS.AUTO, source: 'GovMap — פוליגון חלקה', sourceUrl: govmapViewerUrl, note: 'חושב גיאומטרית מגבולות החלקה',
          });
        }
        if (data?.municipality) {
          p.property.localAuthority = field(data.municipality, { status: STATUS.AUTO, source: 'GovMap' });
          p.property.committee = field(`הוועדה המקומית לתכנון ולבנייה ${data.municipality}`, {
            status: STATUS.AUTO, source: 'נגזר מהרשות המקומית', note: 'ברירת מחדל — יש לאמת בערים עם ועדה מרחבית/מחוזית',
          });
          if (/תל אביב/.test(data.municipality)) {
            p.property.gisUrl = field('https://gisn.tel-aviv.gov.il/iview2js4/index.aspx', {
              status: STATUS.AUTO, source: 'GIS עירוני ת"א', note: 'מערכת ה-GIS העירונית — חפש את הכתובת בתוך הצפייה',
            });
          }
        }
        // GovMap centered on the parcel — clicking the parcel there lists the
        // plans that apply. Per-plan mavat links live in the Plan Stack rows.
        p.property.planningDbUrl = field(`https://www.govmap.gov.il/?c=${Math.round(x_itm)},${Math.round(y_itm)}&z=9`, {
          status: STATUS.AUTO, source: 'GovMap', note: 'מפה ממוקדת בחלקה — לחיצה על החלקה מציגה תכניות חלות',
        });

        // Existing-building facts from GovMap's buildings layer: measured
        // height, ground footprint (תכסית), an ESTIMATED stories count derived
        // from the height, and the actual current use as a site element.
        const bs = data?.buildingStats || null;
        const heightM = Number(bs?.['גובה המבנה במטרים']);
        if (Number.isFinite(heightM) && heightM > 0) {
          p.existingState.summary.height = field(heightM, { status: STATUS.AUTO, source: 'GovMap — שכבת מבנים', sourceUrl: govmapViewerUrl });
          if (!isFilled(p.existingState.summary.storiesAbove)) {
            const floors = Math.max(1, Math.round(heightM / 3));
            p.existingState.summary.storiesAbove = field(floors, {
              status: STATUS.ESTIMATED, source: 'נגזר מגובה המבנה',
              note: `הערכה לפי גובה ${heightM} מ' (כ-3 מ' לקומה) — יש לאמת מול היתרים/מדידה`,
            });
          }
        }
        const footprint = Number(data?.buildingAreaOnParcel?.buildingAreaOnParcel ?? bs?.['שטח במטרים רבועים']);
        if (Number.isFinite(footprint) && footprint > 0) {
          p.existingState.summary.footprint = field(footprint, {
            status: STATUS.AUTO, source: 'GovMap — שכבת מבנים', sourceUrl: govmapViewerUrl, note: 'טביעת רגל קרקע של המבנה (תכסית קיימת)',
          });
        }
        const useGroup = bs?.['קבוצת שימוש'];
        if (useGroup && !p.existingState.siteElements.some((el) => el.type === 'use')) {
          p.existingState.siteElements.push(makeSiteElement({
            type: 'use', description: String(useGroup), status: STATUS.AUTO,
          }));
        }
        // Land-use designation for THIS parcel (retzefmigrashim layer) — the
        // parcel→zone mapping the rights seeding needs. Kept both as a visible
        // field and as structured meta for the autofill logic.
        if (data?.landUse?.designation) {
          const lu = data.landUse;
          p.property.landUseMeta = { designation: lu.designation, plan: lu.plan || null, parcel: lu.parcel || null };
          p.property.landUse = field(lu.designation, {
            status: STATUS.AUTO,
            source: 'GovMap — רצף מגרשים',
            sourceUrl: govmapViewerUrl,
            note: [lu.plan ? `תכנית: ${lu.plan}` : null, lu.parcel ? `מגרש: ${lu.parcel}` : null].filter(Boolean).join(' · ') || null,
          });
        }
      }, `כתובת נפתרה: ${text}`);
      // One gesture, full pull: choosing an address triggers the data fetch
      // directly (the resolved values are passed explicitly — React state
      // hasn't committed yet). The button remains for manual re-pulls.
      onAutofill && onAutofill({
        x_itm, y_itm, gush, helka, address: text,
        landUsePlan: data?.landUse?.plan || null,
      });
    } catch {
      updateProject((p) => {
        p.property.address = field(text, { status: STATUS.MANUAL });
      }, 'כתובת הוזנה (פתרון פרצל נכשל)');
    }
  }, [updateProject, onAutofill]);

  // Split the sources so the user sees what matters: plans assigned a real role
  // in the calc, vs. the citywide/national "unclassified" noise tucked away.
  const relevantPlans = project.planStack.filter((it) => it.role.value !== PLAN_ROLE.UNCLASSIFIED);
  const noisePlans = project.planStack.filter((it) => it.role.value === PLAN_ROLE.UNCLASSIFIED);

  const hasCoords = Number.isFinite(prop.coords?.x_itm) && Number.isFinite(prop.coords?.y_itm);
  // Address-first: until a property is chosen, the address card is the whole
  // screen — everything else only appears once there's something to fill.
  const started = isFilled(prop.address);

  return (
    <div className="rd-property-tab">
      {/* ---- Address resolver ------------------------------------------------ */}
      <section className={`rd-card rd-address-card ${started ? '' : 'is-start'}`}>
        {started ? (
          <>
            <h3 className="rd-card-title">כתובת הנכס</h3>
            <p className="rd-card-sub">בחרו כתובת — הנתונים (גוש/חלקה, תכניות, היתרים ומצב בנוי) יימשכו אוטומטית.</p>
          </>
        ) : (
          <>
            <h3 className="rd-card-title rd-start-title">מתחילים בבחירת כתובת</h3>
            <p className="rd-address-hero-sub">הקלידו את כתובת הנכס — גוש/חלקה, תכניות, היתרים ומצב בנוי יימשכו אוטומטית ותוכלו להתחיל בבדיקת הזכויות.</p>
          </>
        )}
        <div className="rd-address-row">
          <CustomAutocomplete onPlaceSelected={handleAddressSelected} placeholder="הקלידו כתובת בישראל…" />
        </div>
        {autofilling && (
          <div className="rd-loading-banner">
            <span className="rd-spinner" />
            מושך נתונים מהמאגר — תכניות, היתרים ומצב בנוי… (כ-10 שניות)
          </div>
        )}
        {autofillError && <div className="rd-error">{autofillError}</div>}
        {isFilled(prop.address) && !autofilling && (
          <div className="rd-resolved-box">
            <div className="rd-resolved-main">
              <span className="rd-resolved-addr">{prop.address.value}</span>
              <div className="rd-resolved-chips">
                {isFilled(prop.gush) && <span className="rd-chip">גוש {prop.gush.value}</span>}
                {isFilled(prop.helka) && <span className="rd-chip">חלקה {prop.helka.value}</span>}
                {hasCoords && (
                  <span className="rd-chip rd-chip-muted" title="קואורדינטות ברשת ישראל החדשה (EPSG:2039)">
                    ITM {Math.round(prop.coords.x_itm)}, {Math.round(prop.coords.y_itm)}
                  </span>
                )}
                <StatusBadge status={prop.address.status} />
              </div>
              {project.lastPullAt && (
                <div className="rd-pull-receipt">
                  <span>נמשכו: {project.lastPullSummary} · {new Date(project.lastPullAt).toLocaleString('he-IL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  <button
                    type="button"
                    className="rd-refresh-link"
                    onClick={() => onAutofill && onAutofill()}
                    title="משיכה חוזרת — שימושי אם המאגר התעדכן מאז (תכניות חדשות, מסמכים שנסרקו)"
                  >
                    משוך שוב ↻
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {started && <>
      {/* ---- Property fields ------------------------------------------------- */}
      <section className="rd-card">
        <div className="rd-card-head-row" style={{ marginBottom: 16 }}>
          <div>
            <h3 className="rd-card-title">נתוני נכס</h3>
            <p className="rd-card-sub" style={{ margin: '3px 0 0' }}>עברו על הנתונים שחולצו ואשרו אותם — או תקנו ידנית.</p>
          </div>
          <VerifyTools obj={prop} keys={PROP_VERIFY_KEYS} onApproveAll={approveAllProperty} />
        </div>
        <div className="rd-field-group">
          <div className="rd-field-group-title">זיהוי הנכס</div>
          <div className="rd-field-grid">
            <Field label="כתובת" field={prop.address} onChange={(p) => setField('property', 'address', p)} />
            <Field label="גוש" field={prop.gush} anchor="field-gush" onChange={(p) => setField('property', 'gush', p)} />
            <Field label="חלקה" field={prop.helka} anchor="field-helka" onChange={(p) => setField('property', 'helka', p)} />
            <Field label="תתי-חלקות" field={prop.subParcels} onChange={(p) => setField('property', 'subParcels', p)} />
            <Field label='שטח חלקה (מ"ר)' field={prop.parcelArea} anchor="field-parcelArea" type="number" onChange={(p) => setField('property', 'parcelArea', p)} />
          </div>
        </div>
        <div className="rd-field-group">
          <div className="rd-field-group-title">ייעוד ותכנון</div>
          <div className="rd-field-grid">
            <Field label="ייעוד קרקע" field={prop.landUse} anchor="field-landUse" onChange={(p) => setField('property', 'landUse', p)} />
            <Field label="רשות מקומית" field={prop.localAuthority} onChange={(p) => setField('property', 'localAuthority', p)} />
            <Field label="ועדה מקומית" field={prop.committee} anchor="field-committee" onChange={(p) => setField('property', 'committee', p)} />
          </div>
        </div>
        <div className="rd-field-group">
          <div className="rd-field-group-title">קישורים חיצוניים</div>
          <p className="rd-card-sub" style={{ margin: '0 0 12px' }}>קפיצה למקורות הרשמיים לאימות ידני — נפתחים בלשונית חדשה.</p>
          <div className="rd-link-row">
            <LinkChip label="מאגר מידע תכנוני (GovMap)" field={prop.planningDbUrl} />
            <LinkChip label="GIS רשותי" field={prop.gisUrl} />
            <LinkChip label="גוגל מפות" field={prop.gmapsUrl} />
          </div>
        </div>
        <div className="rd-notes-block">
          <Field label="הערות כלליות" field={prop.notes} type="textarea" plain placeholder="הערות חופשיות לבדיקה זו…" onChange={(p) => setField('property', 'notes', p)} />
        </div>
      </section>

      {/* ---- Project fields -------------------------------------------------- */}
      <section className="rd-card">
        <h3 className="rd-card-title">נתוני פרויקט</h3>
        <p className="rd-card-sub">אותו נכס יכול להיבדק עבור יזמים, מטרות ותרחישים שונים — לכן נתוני הפרויקט נפרדים מנתוני הנכס.</p>
        <div className="rd-field-grid">
          <Field label="שם הפרויקט" field={proj.name} plain placeholder="למשל: שלום עליכם 38 — תוספת קומות" onChange={(p) => setField('project', 'name', p)} />
          <Field label="סוג הפרויקט" field={proj.projectType} type="select" plain options={PROJECT_TYPES} onChange={(p) => setField('project', 'projectType', p)} />
          <Field label="מזמין העבודה" field={proj.client} plain placeholder="שם המזמין" onChange={(p) => setField('project', 'client', p)} />
          <Field label="יזם" field={proj.developer} plain placeholder="שם היזם" onChange={(p) => setField('project', 'developer', p)} />
          <Field label="בעלי זכויות ודיירים קיימים" field={proj.rightsHolders} plain placeholder="שמות / הערות" onChange={(p) => setField('project', 'rightsHolders', p)} />
          <Field label="אדריכל / מודד / שמאי / עו״ד / יועצים" field={proj.consultants} plain placeholder="שמות / הערות" onChange={(p) => setField('project', 'consultants', p)} />
          <Field label="אנשי קשר" field={proj.contacts} plain onChange={(p) => setField('project', 'contacts', p)} placeholder="שם · תפקיד · טלפון / מייל" />
        </div>
      </section>

      {/* ---- Document library ------------------------------------------------ */}
      <section className="rd-card">
        <div className="rd-card-head-row">
          <h3 className="rd-card-title">ספריית מסמכים</h3>
          <button type="button" className="rd-mini-btn" onClick={addDocument}>+ מסמך</button>
        </div>
        <p className="rd-card-sub">מסמכים שהתקבלו מהיזם: נסח טאבו, מדידה, היתרים, תמונות וסיכומי פגישות. הוספת נסח טאבו מסירה את החוסר מהדוח.</p>
        {project.documents.length === 0 ? (
          <div className="rd-dropzone">
            <span>אין מסמכים בספרייה עדיין</span>
            <button type="button" className="rd-autofill-btn" onClick={addDocument}>+ הוסף מסמך</button>
          </div>
        ) : (
          <table className="rd-table">
            <thead>
              <tr><th>סוג</th><th>שם / תיאור</th><th>קישור</th><th>סטטוס</th><th></th></tr>
            </thead>
            <tbody>
              {project.documents.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <select className="rd-input rd-input-sm" value={doc.kind || 'אחר'} onChange={(e) => updateDocument(doc.id, { kind: e.target.value })}>
                      {DOC_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </td>
                  <td>
                    <input className="rd-input rd-input-sm" value={doc.name} placeholder="שם המסמך" onChange={(e) => updateDocument(doc.id, { name: e.target.value })} />
                  </td>
                  <td className="rd-doc-url-cell">
                    <input className="rd-input rd-input-sm" value={doc.url || ''} placeholder="https://..." dir="ltr" onChange={(e) => updateDocument(doc.id, { url: e.target.value || null })} />
                    {doc.url && <a href={doc.url} target="_blank" rel="noreferrer" title="פתח מסמך">↗</a>}
                  </td>
                  <td><StatusBadge status={doc.status} /></td>
                  <td><button type="button" className="rd-remove-btn" onClick={() => removeDocument(doc.id)} title="הסר">✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ---- Plan Stack ------------------------------------------------------ */}
      <section className="rd-card" data-rd-anchor="card-planstack">
        <h3 className="rd-card-title">Plan Stack — ערימת תכניות ומקורות</h3>
        <p className="rd-card-sub">
          המאגר מצא {project.planStack.length} תכניות שנוגעות לחלקה. לא כולן משפיעות באותה מידה —
          לכל תכנית קבעו את <b>תפקידה בחישוב</b> (עמודת ״תפקיד בחישוב״). המערכת ניחשה תפקיד ראשוני; אשרו או תקנו.
        </p>
        {project.planStack.length > 0 && (
          <div className="rd-plan-legend">
            <span className="rd-plan-legend-item"><i className="rd-legend-dot main" /> <b>מקור זכויות ראשי</b> — ממנה נגזרות הזכויות (בד״כ אחת)</span>
            <span className="rd-plan-legend-item"><i className="rd-legend-dot mod" /> <b>תכנית משלימה</b> — מוסיפה/משנה זכויות (למשל תמ״א 38)</span>
            <span className="rd-plan-legend-item"><i className="rd-legend-dot pot" /> <b>פוטנציאל / תקרה</b> — נספר בנפרד, אינו זכות מוקנית</span>
            <span className="rd-plan-legend-item"><i className="rd-legend-dot inf" /> <b>שכבת השפעה / מגבלה</b> — מגביל, אינו מוסיף זכויות</span>
          </div>
        )}
        {project.planStack.length > 0 && (
          <p className="rd-card-warn">⚠ "זכויות שחולצו" מציגות את כלל אזורי התכנית — לא בהכרח האזור החל על חלקה זו. יש לאמת מול התשריט.</p>
        )}
        {project.planStack.length === 0 ? (
          <div className="rd-empty">אין עדיין תכניות. בחר כתובת ולחץ "משוך נתונים מהמאגר".</div>
        ) : (
          <>
            <table className="rd-table">
              <thead>
                <tr>
                  <th>מקור</th>
                  <th>סטטוס נתון</th>
                  <th>תפקיד בחישוב</th>
                  <th>זכויות שחולצו</th>
                  <th>תכנית</th>
                </tr>
              </thead>
              <tbody>
                {relevantPlans.length === 0 ? (
                  <tr><td colSpan={5} className="rd-empty" style={{ padding: '14px 12px' }}>אף תכנית לא סווגה עדיין כמשפיעה על החישוב — סווגו תכנית כ״מקור זכויות ראשי״.</td></tr>
                ) : relevantPlans.map((item) => (
                  <PlanRow key={item.id} item={item} setRole={setRole} primary={item.role.value === PLAN_ROLE.MAIN_RIGHTS} />
                ))}
              </tbody>
            </table>

            {noisePlans.length > 0 && (
              <details className="rd-plan-noise">
                <summary>{noisePlans.length} תכניות נוספות — ארציות/עירוניות שאינן נוגעות לחישוב (לחצו להצגה ולסיווג מחדש)</summary>
                <table className="rd-table" style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      <th>מקור</th>
                      <th>סטטוס נתון</th>
                      <th>תפקיד בחישוב</th>
                      <th>זכויות שחולצו</th>
                      <th>תכנית</th>
                    </tr>
                  </thead>
                  <tbody>
                    {noisePlans.map((item) => (
                      <PlanRow key={item.id} item={item} setRole={setRole} primary={false} />
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </>
        )}
      </section>
      </>}
    </div>
  );
}

// Auto-generated jump-off links: rendered as a tappable chip when resolved,
// muted/disabled when the source didn't apply (e.g. GIS only for Tel Aviv).
function LinkChip({ label, field }) {
  const url = isFilled(field) ? field.value : null;
  if (!url) return <span className="rd-link-chip is-empty" title="לא זמין לכתובת זו">{label}</span>;
  return (
    <a className="rd-link-chip" href={url} target="_blank" rel="noreferrer" dir="rtl">
      {label} <span className="rd-link-chip-arrow">↗</span>
    </a>
  );
}

const PROP_VERIFY_KEYS = ['address', 'gush', 'helka', 'subParcels', 'parcelArea', 'landUse', 'localAuthority', 'committee'];

// One Plan Stack row — shared by the relevant list and the collapsed noise list.
function PlanRow({ item, setRole, primary }) {
  return (
    <tr className={primary ? 'rd-plan-row-main' : ''}>
      <td>
        <div className="rd-plan-title">
          {item.title.value || item.planRef || '—'}
          {primary && <span className="rd-plan-main-badge">מקור ראשי לחישוב</span>}
          {planAgeYear(item) && (
            <span className="rd-oldplan-chip" title="תכנית ותיקה — שיטת החישוב והזכויות עשויות להיות שונות מהמקובל כיום">
              תכנית ישנה ({planAgeYear(item)})
            </span>
          )}
        </div>
        {item.type.value && <div className="rd-plan-type">{item.type.value}</div>}
        {item.approvalDate && <div className="rd-plan-date">אישור: {String(item.approvalDate).slice(0, 10)}</div>}
      </td>
      <td><StatusBadge status={item.role.status} /></td>
      <td>
        <select
          className="rd-input rd-input-sm"
          value={item.role.value || ''}
          onChange={(e) => setRole(item.id, { value: e.target.value, status: STATUS.APPROVED })}
        >
          {PLAN_ROLE_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </td>
      <td className="rd-plan-rights"><ZonesCell zones={item.zones} /></td>
      <td>
        {item.planUrl
          ? <a href={item.planUrl} target="_blank" rel="noreferrer">צפה ↗</a>
          : <span className="rd-muted" title="אין קישור ציבורי לתכנית זו במאגר">—</span>}
      </td>
    </tr>
  );
}

// Year chip for pre-1980 plans — old plans use different rights semantics
// (per-floor coverage), so their age is load-bearing information.
function planAgeYear(item) {
  const year = Number(String(item.approvalDate || '').slice(0, 4));
  return year && year < 1980 ? year : null;
}

const USE_LABELS_HE = {
  residential: 'מגורים',
  commercial: 'מסחר',
  industrial: 'תעשייה',
  mixed: 'מעורב',
  public: 'ציבור',
  open_space: 'שטח פתוח',
  other: 'אחר',
};

function zoneLine(z) {
  const bits = [];
  if (z.zone_code) bits.push(`אזור ${z.zone_code}`);
  if (z.primary_use) bits.push(USE_LABELS_HE[z.primary_use] || z.primary_use);
  if (z.max_far_pct != null) bits.push(`זכויות ${z.max_far_pct}%`);
  if (z.max_height_m != null) bits.push(`גובה ${z.max_height_m} מ'`);
  if (z.max_stories != null) bits.push(`${z.max_stories} קומות`);
  return bits.join(' · ');
}

// Collapsed by default, and labeled "כל אזורי התכנית · לא מסונן לחלקה" so the
// full plan-wide zone table can't be mistaken for this parcel's rights.
function ZonesCell({ zones = [] }) {
  const lines = zones.map(zoneLine).filter(Boolean);
  if (!lines.length) return <span className="rd-muted">—</span>;
  return (
    <details className="rd-zones">
      <summary>
        {zones.length} אזורים בתכנית
        <span className="rd-zones-caption"> · לא מסונן לחלקה</span>
      </summary>
      <ul className="rd-zones-list">
        {lines.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </details>
  );
}
